import React, { useEffect, useState, useRef } from 'react';
import { apiFetch } from '../lib/api';
import { guessCarrier as guessShape } from '../lib/carrierGuess'; // @@CLEANUP_v90@@ one shared guesser

// Defaults the dropdown from the tracking number's shape; ambiguous -> 'other'
// (user can always override).
function guessCarrier(num) {
  return guessShape(num) || 'other';
}
const CARRIER_OPTS = [['other', 'Other / none'], ['fedex', 'FedEx'], ['ups', 'UPS'], ['usps', 'USPS']];
const SHIP_STATUS = {
  label_created: 'Label created', in_transit: 'In transit', out_for_delivery: 'Out for delivery',
  ready_for_pickup: 'Ready for pickup', delivered: 'Delivered', exception: 'Exception',
};

// Outbound shipments for a production job. Admin/manager can add (option or
// uploaded label), download, and delete. Others see a read-only list.
export default function JobShipments({ jobId, canManage }) {
  const [shipments, setShipments] = useState([]);
  const [options, setOptions] = useState([]);
  const [optionId, setOptionId] = useState('');
  const [tracking, setTracking] = useState('');
  const [carrier, setCarrier] = useState('other');
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef(null);

  async function load() {
    const res = await apiFetch(`/api/production/${jobId}/shipments`);
    if (res.ok) { const d = await res.json(); setShipments(d.shipments || []); }
  }
  async function loadOptions() {
    const res = await apiFetch('/api/shipping-options');
    if (res.ok) { const d = await res.json(); setOptions(d.options || []); }
  }
  useEffect(() => { load(); if (canManage) loadOptions(); /* eslint-disable-next-line */ }, [jobId]);

  function onTrackingChange(v) {
    setTracking(v);
    if (carrier === 'other') { const g = guessCarrier(v); if (g !== 'other') setCarrier(g); }
  }

  async function refreshTracking() {
    setRefreshBusy(true); setRefreshMsg('');
    try {
      const res = await apiFetch(`/api/production/${jobId}/shipments/refresh`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { setRefreshMsg(`Checked ${d.total}: ${d.changed} updated, ${d.skipped} no data.`); load(); }
      else setRefreshMsg(d.error || 'Refresh failed.');
    } catch (e) { setRefreshMsg('Could not reach the server.'); }
    finally { setRefreshBusy(false); }
  }

  async function setShipmentCarrier(shipmentId, c) {
    await apiFetch(`/api/production/${jobId}/shipments/${shipmentId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ carrier: c }),
    });
    load();
  }

  async function addOption() {
    setMsg('');
    if (!optionId) { setMsg('Pick a shipping option.'); return; }
    const res = await apiFetch(`/api/production/${jobId}/shipments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shipping_option_id: optionId, tracking_number: tracking.trim() || undefined, carrier }),
    });
    if (res.ok) { setOptionId(''); setTracking(''); setCarrier('other'); load(); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not add.'); }
  }

  async function uploadLabel(file) {
    if (!file) return;
    setBusy(true); setMsg('');
    try {
      const res = await apiFetch(`/api/production/${jobId}/shipments/label`, {
        method: 'POST',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'x-filename': file.name || 'label',
          ...(tracking.trim() ? { 'x-tracking-number': tracking.trim() } : {}),
          ...(carrier && carrier !== 'other' ? { 'x-carrier': carrier } : {}),
        },
        body: file,
      });
      if (res.ok) { setTracking(''); if (fileRef.current) fileRef.current.value = ''; load(); }
      else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Upload failed.'); }
    } finally { setBusy(false); }
  }

  async function download(sh) {
    const res = await apiFetch(`/api/production/${jobId}/shipments/${sh.id}/label`);
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = sh.original_name || 'label';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  async function remove(sh) {
    if (!window.confirm('Remove this shipment?')) return;
    const res = await apiFetch(`/api/production/${jobId}/shipments/${sh.id}`, { method: 'DELETE' });
    if (res.ok) load();
  }

  function methodLabel(sh) {
    if (sh.method === 'option') return sh.option_label || 'Shipping option';
    return sh.original_name || 'Uploaded label';
  }

  return (
    <div className="hq-panel" style={{ marginBottom: 20 }}>
      <div className="hq-panel-head">
        <h2>Outbound shipping</h2>
        <button className="hq-btn hq-btn-ghost" onClick={refreshTracking} disabled={refreshBusy}>
          {refreshBusy ? 'Checking…' : 'Refresh tracking'}
        </button>
      </div>

      {canManage && (
        <div style={{ padding: 20, borderBottom: '1px solid var(--hq-line)' }}>
          <div className="hq-form" style={{ marginBottom: 12 }}>
            <select className="hq-select" value={optionId} onChange={(e) => setOptionId(e.target.value)} style={{ minWidth: 200 }}>
              <option value="">— Choose shipping option —</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <select className="hq-select" value={carrier} onChange={(e) => setCarrier(e.target.value)} style={{ minWidth: 130 }}>
              {CARRIER_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input className="hq-input" placeholder="Tracking # (optional)" value={tracking}
              onChange={(e) => onTrackingChange(e.target.value)} style={{ minWidth: 180 }} />
            <button className="hq-btn" onClick={addOption}>Add</button>
          </div>
          <div className="hq-form">
            <input ref={fileRef} type="file" accept="application/pdf,image/png"
              onChange={(e) => uploadLabel(e.target.files && e.target.files[0])}
              disabled={busy} className="hq-input" style={{ minWidth: 240, paddingTop: 7 }} />
            <span style={{ color: 'var(--hq-muted)', fontSize: 12 }}>
              {busy ? 'Uploading…' : 'or upload a label (PDF or PNG)'}
            </span>
          </div>
          {msg && <p className="hq-msg">{msg}</p>}
        </div>
      )}

      <table className="hq-table">
        <thead><tr><th>Method</th><th>Carrier</th><th>Tracking</th><th>Status</th><th>ETA</th><th style={{ width: 160 }}></th></tr></thead>
        <tbody>
          {shipments.map((sh) => (
            <tr key={sh.id}>
              <td>{methodLabel(sh)}</td>
              <td>
                {canManage ? (
                  <select className="hq-select" value={sh.carrier || 'other'}
                    onChange={(e) => setShipmentCarrier(sh.id, e.target.value)} style={{ height: 30, minWidth: 110 }}>
                    {CARRIER_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                ) : (sh.carrier && sh.carrier !== 'other' ? sh.carrier.toUpperCase() : '—')}
              </td>
              <td>{sh.tracking_number || '—'}</td>
              <td>{sh.status ? (SHIP_STATUS[sh.status] || sh.status) : '—'}</td>
              <td>{sh.estimated_delivery ? String(sh.estimated_delivery).slice(0, 10) : '—'}</td>
              <td>
                {sh.has_file && (
                  <button className="hq-signout" style={{ width: 'auto', padding: '0 12px', marginRight: 8 }}
                    onClick={() => download(sh)}>Download</button>
                )}
                {canManage && (
                  <button className="hq-signout" style={{ width: 'auto', padding: '0 12px', borderColor: 'rgba(255,90,82,.3)', color: '#ff5a52' }}
                    onClick={() => remove(sh)}>Remove</button>
                )}
              </td>
            </tr>
          ))}
          {shipments.length === 0 && (
            <tr><td className="hq-empty" colSpan={6}>No outbound shipments yet.</td></tr>
          )}
        </tbody>
      </table>
      {refreshMsg && <p className="hq-msg" style={{ margin: '0 20px 16px' }}>{refreshMsg}</p>}
    </div>
  );
}
